import React, { useState } from "react";
import Breadcrumb from "../../../Components/Common/Breadcrumb";
import { faAdd } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../Components/Common/Modal";
import MaterialForm from "./Components/MaterialForm";
import useToastError from "../../../hooks/useErrorToast";
import { useLocation } from "react-router-dom";
import useSWRMutation from "swr/mutation";
import { MaterialApiUrls } from "../../../Api/Auth/ApiUrls";
import { toast } from "react-toastify";
import {
  sendPostRequestAuth,
  sendPutRequestAuth,
} from "../../../Api/swrConfig";
import Storage from "../Storage";
import Table from "../../../Components/Common/Table";
import TableHead from "../../../Components/Common/TableHead";
import useTable from "../../../hooks/useTable";
import QueryString from "qs";
import useSWR from "swr";
import NoData from "../../../Components/Common/NoData";
import { Loader } from "../../../Components/Common/Loader";
import MaterialRow from "./Components/MaterialRow";
import Pagination from "../../../Components/Common/Pagination/Pagination";

const tableColumnData = [
  { id: "title", label: "Title" },
  { id: "free", label: "Free" },
  { id: "subscription", label: "Subscription" },
  { id: "category", label: "Category" },
  { id: "price", label: "Price" },
  { id: "link", label: "Link" },
  { id: "createdAt", label: "Created" },
];
const MaterialList = () => {
  const [row, setRow] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    free: "free",
    price: 0,
    subscription: "no",
    link: "",
    category: null,
    actionButton: "",
  });
  const [isModalOpen, setModalOpen] = useState(false);
  const [isStorageModalOpen, setStorageModalOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const { showErrorToast } = useToastError();
  const location = useLocation();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useTable({ defaultOrderBy: "" });

  const apiQueryParams = {
    offset: (page - 1) * rowsPerPage,
    limit: rowsPerPage,
  };
  const apiQueryString = QueryString.stringify(apiQueryParams);
  const { isLoading, data, mutate } = useSWR(
    MaterialApiUrls.list(apiQueryString)
  );

  const handleInputChange = (name, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value, // Set only the field that's changed
    }));
  };

  const onEditClick = (row) => {
    setRow(row);
    handleInputChange("title", row.title);
    handleInputChange("free", row.free === false ? "paid" : "free");
    handleInputChange(
      "subscription",
      row.subscription === false ? "no" : "yes"
    );
    handleInputChange("price", row.price);
    handleInputChange("link", row.link);
    handleInputChange("category", row.category?._id);
    handleInputChange("actionButton", row.actionButton);
    setModalOpen(true);
  };

  const { trigger, isMutating } = useSWRMutation(
    MaterialApiUrls.create,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );
  const { trigger: EditTrigger, isMutating: editMutating } = useSWRMutation(
    MaterialApiUrls.edit(row?._id),
    sendPutRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );

  const actionButtons = [
    {
      label: "Create",
      icon: faAdd,
      onClick: () => {
        setRow(null);
        setFormData({
          title: "",
          free: "free",
          subscription: "no",
          price: 0,
          link: "",
          category: null,
          actionButton: "",
        });
        setModalOpen(true);
      },
    },
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title) newErrors.title = "Title is required";
    if (!formData.category) newErrors.category = "Category is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    const modifiedData = {
      ...formData,
      category:
        typeof formData.category === "object"
          ? formData.category.value
          : formData.category,
      free: formData.free === "free",
      subscription: formData.subscription === "yes",
    };

    row ? EditTrigger(modifiedData) : trigger(modifiedData);
  };

  return (
    <div>
      <Breadcrumb actionButtons={actionButtons} />
      <Table>
        <TableHead rowCount={data?.data.length} headerData={tableColumnData} />
        <tbody className={`divide-y divide-gray-200`}>
          {!isLoading ? (
            <>
              {data?.data.map((row) => (
                <MaterialRow
                  key={row._id}
                  row={row}
                  mutate={mutate}
                  onEditClick={onEditClick}
                />
              ))}
            </>
          ) : (
            <tr>
              <td colSpan="100" className="text-center py-4">
                <Loader />
              </td>
            </tr>
          )}
        </tbody>
        <NoData isNotFound={!data?.data.length && !isLoading} />
      </Table>
      <Pagination
        totalRows={data?.pagination.totalItems || 0}
        currentPage={page}
        rowsPerPage={rowsPerPage}
        currentPath={location.pathname}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Modal
        isOpen={isModalOpen}
        title={row ? "Edit Material" : "Create Material"}
        onConfirm={handleSubmit}
        onCancel={() => {
          setErrors({});
          setModalOpen(false);
        }}
        isLoading={isMutating || editMutating}
        formWidth
      >
        <MaterialForm
          errors={errors}
          handleSubmit={handleSubmit}
          onChange={handleInputChange}
          formData={formData}
          setModalOpen={setStorageModalOpen}
        />
      </Modal>
      <Modal
        isOpen={isStorageModalOpen}
        title={"Storage Manager"}
        // onConfirm={handleSubmit}
        onCancel={() => {
          setStorageModalOpen(false);
        }}
        footer={false}
      >
        <Storage
          isSelector
          onConfirm={(file) => {
            if (Array.isArray(file) && file.length)
              handleInputChange("link", file[0]?.fileUrl);
            setStorageModalOpen(false);
          }}
        />
      </Modal>
    </div>
  );
};

export default MaterialList;
